<script setup>
  import Drawer from 'primevue/drawer';
  import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import { HOME_NAVBAR } from '@/constants/home';

  import burgerIcon from '@/assets/images/home/burger.svg';
  import closeIcon from '@/assets/images/home/close.svg';

  const ACTIVE_SECTION_THRESHOLD = 0.35;

  const route = useRoute();
  const router = useRouter();
  const isHomePage = computed(() => route.path === '/');
  const isBlogPage = computed(() => route.path.startsWith(HOME_NAVBAR.blogLink.to));

  const isMenuOpen = ref(false);
  const isScrolled = ref(false);
  const activeSectionId = ref(null);
  let scrollFrame = null;

  const updateActiveSection = () => {
    const threshold = window.innerHeight * ACTIVE_SECTION_THRESHOLD;
    const passedLinks = HOME_NAVBAR.links.filter(({ id }) => {
      const section = document.getElementById(id);
      return section && section.getBoundingClientRect().top <= threshold;
    });
    activeSectionId.value = passedLinks.at(-1)?.id ?? null;
  };

  const handleScroll = () => {
    isScrolled.value = window.scrollY > 0;
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;
      updateActiveSection();
    });
  };

  const scrollToSection = (id) => {
    isMenuOpen.value = false;
    const hash = `#${id}`;
    if (!isHomePage.value) {
      // the homepage scrolls to the hash itself once its sections are mounted
      router.push({ path: '/', hash });
      return;
    }
    router.replace({ hash });
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    isMenuOpen.value = false;
    if (isHomePage.value) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const BLOG_LINK_KEY = 'blog';
  const desktopLinks = {};
  const setDesktopLink = (key) => (el) => {
    desktopLinks[key] = el?.$el ?? el;
  };

  const activeLinkKey = computed(() => {
    if (isHomePage.value) return activeSectionId.value;
    return isBlogPage.value ? BLOG_LINK_KEY : null;
  });

  const indicator = reactive({ left: 0, width: 0, isVisible: false, slides: false });
  const indicatorClasses = computed(() => [
    indicator.slides ? 'transition-[translate,width,opacity]' : 'transition-opacity',
    indicator.isVisible ? 'opacity-100' : 'opacity-0',
  ]);

  const updateIndicator = () => {
    const link = desktopLinks[activeLinkKey.value];
    if (!link?.offsetWidth) {
      indicator.isVisible = false;
      return;
    }
    // slide only between links; the first appearance shows it in place
    indicator.slides = indicator.isVisible;
    indicator.left = link.offsetLeft;
    indicator.width = link.offsetWidth;
    indicator.isVisible = true;
  };

  watch(activeLinkKey, () => nextTick(updateIndicator));

  onMounted(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateIndicator);
    nextTick(updateIndicator);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', updateIndicator);
    cancelAnimationFrame(scrollFrame);
  });
</script>

<template>
  <!-- pr: PrimeVue pads <body> by the scrollbar width while a dialog locks scroll; fixed elements ignore it -->
  <header
    :class="[
      'fixed inset-x-0 top-0 z-1020 pr-(--p-scrollbar-width) transition-[background-color,box-shadow] duration-100',
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent',
    ]"
  >
    <nav
      class="mx-auto flex h-18 w-full max-w-299 items-center justify-between px-8 md:h-21 lg:h-25 xl:px-0"
      :aria-label="HOME_NAVBAR.label"
    >
      <router-link to="/" class="shrink-0" @click="scrollToTop">
        <img src="/logo-blue.svg" :alt="HOME_NAVBAR.logoAlt" class="h-9 w-auto md:h-11 lg:h-13" />
      </router-link>

      <div class="relative hidden lg:block">
        <ul class="flex items-center gap-8 xl:gap-13">
          <li v-for="link in HOME_NAVBAR.links" :key="link.id">
            <a
              :ref="setDesktopLink(link.id)"
              :href="`/#${link.id}`"
              :class="[
                'hover:text-blue-light text-[15px]/4.5 font-semibold transition-colors',
                activeSectionId === link.id ? 'text-blue-light' : 'text-black',
              ]"
              :aria-current="activeSectionId === link.id ? 'location' : undefined"
              @click.prevent="scrollToSection(link.id)"
              >{{ link.title }}</a
            >
          </li>
          <li>
            <router-link
              :ref="setDesktopLink(BLOG_LINK_KEY)"
              :to="HOME_NAVBAR.blogLink.to"
              :class="[
                'hover:text-blue-light text-[15px]/4.5 font-semibold transition-colors',
                isBlogPage ? 'text-blue-light' : 'text-black',
              ]"
              :aria-current="isBlogPage ? 'page' : undefined"
              >{{ HOME_NAVBAR.blogLink.title }}</router-link
            >
          </li>
        </ul>
        <span
          :class="[
            'bg-blue-light pointer-events-none absolute top-full left-0 mt-2 h-0.5 rounded-full duration-300 motion-reduce:transition-none',
            indicatorClasses,
          ]"
          :style="{ width: `${indicator.width}px`, translate: `${indicator.left}px 0` }"
          aria-hidden="true"
        />
      </div>

      <button
        type="button"
        class="-mr-1 cursor-pointer p-1 lg:hidden"
        :aria-label="HOME_NAVBAR.openMenuLabel"
        @click="isMenuOpen = true"
      >
        <img :src="burgerIcon" alt="" class="size-7" />
      </button>
    </nav>

    <Drawer v-model:visible="isMenuOpen" position="top" style="height: auto">
      <template #container="{ closeCallback }">
        <div class="bg-white px-8 pb-10">
          <div class="flex h-18 items-center justify-between">
            <router-link to="/" class="shrink-0" @click="scrollToTop">
              <img src="/logo-blue.svg" :alt="HOME_NAVBAR.logoAlt" class="h-9 w-auto" />
            </router-link>
            <button
              type="button"
              class="-mr-1 cursor-pointer p-1"
              :aria-label="HOME_NAVBAR.closeMenuLabel"
              @click="closeCallback"
            >
              <img :src="closeIcon" alt="" class="size-7" />
            </button>
          </div>

          <ul class="mt-6 flex flex-col items-center gap-7">
            <li v-for="link in HOME_NAVBAR.links" :key="link.id">
              <a
                :href="`/#${link.id}`"
                :class="[
                  'hover:text-blue-light text-lg font-semibold transition-colors',
                  activeSectionId === link.id ? 'text-blue-light' : 'text-black',
                ]"
                :aria-current="activeSectionId === link.id ? 'location' : undefined"
                @click.prevent="scrollToSection(link.id)"
                >{{ link.title }}</a
              >
            </li>
            <li>
              <router-link
                :to="HOME_NAVBAR.blogLink.to"
                :class="[
                  'hover:text-blue-light text-lg font-semibold transition-colors',
                  isBlogPage ? 'text-blue-light' : 'text-black',
                ]"
                :aria-current="isBlogPage ? 'page' : undefined"
                @click="isMenuOpen = false"
                >{{ HOME_NAVBAR.blogLink.title }}</router-link
              >
            </li>
          </ul>
        </div>
      </template>
    </Drawer>
  </header>
</template>
